import { defineStore } from 'pinia';
import {
    ligacoesService,
    type DirecaoLigacao,
    type LigacaoAtualizadaPayload,
    type SituacaoLigacao,
} from '@/services/ligacoes/ligacoes.service';
import { LigacaoAudio, type LigacaoAudioEstado } from '@/services/ligacoes/ligacao-audio';
import { definirChamando, prepararTons, tocarFim } from '@/services/ligacoes/tons-de-ligacao';
import { textoDeLigacao } from '@/services/mensagens/mensagens.service';
import { WebsocketService } from '@/services/websocket/websocket.service';
import { useAuthStoreUser } from '@/stores/app/authUser';

/** Uma ligação como a tela a vê. */
export type Ligacao = {
    idInbox:       string;
    idLigacao:     string;
    idContato:     string | null;
    idAtendimento: string | null;
    codigo:        string;
    nome:          string | null;
    /** Foto do contato, quando o servidor conhece uma. */
    imagem:        string | null;
    situacao:      SituacaoLigacao;
    ehVideo:       boolean;
    data:          string;
    /**
     * Quem originou. Decide o card: só `RECEBIDA` pode ser atendida, e o
     * servidor recusa `atender` numa `REALIZADA` (já nasce com dono).
     */
    direcao:       DirecaoLigacao;
    /** Dono — quem atendeu ou quem discou. `null` enquanto uma recebida toca. */
    idUsuario:     string | null;
    /** Âncora do cronômetro. `null` enquanto toca e em ligação não atendida. */
    dataAtendimento: string | null;
    /** Duração final, em segundos. `null` = não houve fala. */
    duracaoSegundos: number | null;
    /** Preenchido só depois de atender: estado do canal de áudio. */
    audio?:        LigacaoAudioEstado;
    erro?:         string | null;
};

/** Situações em que a ligação já morreu e não deve mais ocupar a tela. */
const TERMINAIS: readonly SituacaoLigacao[] = ['PERDIDA', 'RECUSADA', 'ENCERRADA'];

/** Quanto tempo uma ligação encerrada fica visível antes de sair sozinha (ms). */
const MS_ANTES_DE_LIMPAR = 6000;

/**
 * Teto para uma ligação ficar TOCANDO sem notícia nenhuma (ms).
 *
 * O WhatsApp desiste de uma oferta em torno de 60 s, então nada legítimo toca
 * por dois minutos. Existe porque o estado do card vive só de evento: se o
 * `terminate` se perder — socket caindo no meio da chamada, `fecharOrfas`
 * fechando a linha no banco (que não publica NADA), o `reject reason="enc"`
 * engolido pelo gateway — a ligação ficava TOCANDO para sempre, e agora isso
 * não é mais só um card: é um selo piscando na lista de conversas, escondendo
 * a prévia da conversa até alguém recarregar a página.
 */
const MS_MAX_TOCANDO = 120_000;

/**
 * Atender uma segunda ligação põe as anteriores em espera automaticamente.
 *
 * **DESLIGADO PARA TESTE** — com `false`, TODAS as ligações atendidas ficam com
 * áudio ao vivo ao mesmo tempo. É o que permite verificar se o WhatsApp aguenta
 * dois fluxos de mídia concorrentes no mesmo número.
 *
 * O que acontece com `false` (não é bug, é a consequência):
 *   - as vozes das duas pessoas saem MISTURADAS no fone do operador;
 *   - o microfone do operador vai para as DUAS ligações simultaneamente, ou
 *     seja, as duas pessoas ouvem uma à outra através de você.
 *
 * `true` = comportamento de telefone com duas linhas (só uma fala por vez).
 * A espera MANUAL (botão Retomar / `focar`) continua funcionando nos dois modos.
 */
const ESPERA_AUTOMATICA = false;

/**
 * Sessões de áudio vivas, por `idLigacao`. Fora do state do Pinia de propósito:
 * `LigacaoAudio` carrega AudioContext, MediaStream e WebSocket — não é
 * serializável e não deve ser reativo.
 *
 * Era um singleton (`let audio`), o que limitava a UMA ligação com áudio. O
 * WhatsApp ENTREGA a segunda oferta a um número já em ligação (verificado em
 * 2026-09-03: 14 s de sobreposição no log do gateway), então o limite era só
 * nosso.
 */
const audios = new Map<string, LigacaoAudio>();
const timersDeLimpeza = new Map<string, ReturnType<typeof setTimeout>>();
/** Watchdogs de "tocando sem fim", por `idLigacao`. Ver `MS_MAX_TOCANDO`. */
const timersDeToque = new Map<string, ReturnType<typeof setTimeout>>();

export const useLigacoesStore = defineStore('ligacoes', {
    state: () => ({
        /** Ligações vivas por `idLigacao`. */
        ligacoes: {} as Record<string, Ligacao>,
        /**
         * `idLigacao` das ligações com áudio ativo, na ordem em que foram
         * atendidas. Pode ter mais de uma: só UMA fica em foco, as outras vão
         * para espera (microfone e reprodução suspensos).
         */
        emAndamento: [] as string[],
        /** `idLigacao` em FOCO — a que ouve e fala. As demais estão em espera. */
        emFoco: null as string | null,
        /** Mute do microfone, por ligação. */
        mutados: {} as Record<string, boolean>,
        /**
         * Estado de ESPERA por ligação. Registrado à parte de `emFoco` de
         * propósito: com `ESPERA_AUTOMATICA = false` várias ligações ficam ativas
         * ao mesmo tempo, e "não está em foco" deixa de significar "está em
         * espera" — a tela mostraria "Em espera" numa ligação que está ouvindo.
         */
        emEspera: {} as Record<string, boolean>,
        nivelMic: 0,
        registrado: false,
        /**
         * `idInbox` com discagem em voo. Guarda contra o duplo-clique no botão:
         * duas discagens seguidas geram DUAS ligações reais, e a segunda ainda
         * toma 409 ("você já está em uma ligação") — deixando uma chamada viva
         * no telefone do cliente e um erro na tela.
         */
        discando: {} as Record<string, boolean>,
    }),

    getters: {
        /**
         * Todas as RECEBIDAS que estão TOCANDO e ainda não foram atendidas.
         * Array, não uma só: o WhatsApp entrega ofertas concorrentes para o
         * mesmo número.
         *
         * O filtro por direção é obrigatório: sem ele a ligação que ESTE
         * operador acabou de discar aparecia aqui como "Ligação recebida" com
         * botão Atender (que devolve 409 e derruba o áudio recém-aberto) — e a
         * discada por um COLEGA aparecia igual para todo mundo da inbox, já que
         * o evento é publicado na sala da inbox.
         */
        tocando(state): Ligacao[] {
            return Object.values(state.ligacoes).filter(
                l => l.situacao === 'TOCANDO'
                    && l.direcao !== 'REALIZADA'
                    && !state.emAndamento.includes(l.idLigacao),
            );
        },
        /**
         * Ligações EM CURSO deste operador — as que o card precisa mostrar
         * enquanto a conversa existe.
         *
         * A lista NÃO é mais só `emAndamento`. `emAndamento` é contabilidade do
         * ÁUDIO (quem tem WebSocket aberto), e amarrar a visibilidade a ela
         * fazia uma ligação VIVA sumir da tela: basta o servidor já ter marcado
         * `ATENDIDA` e o nosso lado não ter a sessão de áudio.
         *
         * O caso real é o atendimento confirmado por OUTRO caminho — outro
         * dispositivo, ou o evento do servidor chegando antes de a sessão subir.
         * (`atender` que falha no HTTP depois de abrir o som NÃO é um deles: ali
         * `situacao` nunca chega a virar `ATENDIDA`, então a linha volta por
         * `tocando`, com o botão e a mensagem de erro. A primeira versão deste
         * comentário afirmava o contrário.)
         *
         * Nesses casos a chamada saía de `tocando` (não está mais TOCANDO) sem
         * entrar em `ativas`, e o operador ficava falando ao telefone olhando
         * para uma tela vazia, sem nem o botão de encerrar.
         *
         * Agora manda o ESTADO DA CHAMADA: atendida, não terminal e minha.
         * O áudio virou atributo do card (ver `temAudio`), não condição para
         * ele existir.
         */
        ativas(state): Ligacao[] {
            const eu = useAuthStoreUser().user?.id ?? null;
            const lista = state.emAndamento
                .map(id => state.ligacoes[id])
                .filter((l): l is Ligacao => !!l);

            const jaListadas = new Set(state.emAndamento);
            for (const ligacao of Object.values(state.ligacoes)) {
                if (jaListadas.has(ligacao.idLigacao)) continue;
                // `!== 'ATENDIDA'` já exclui as terminais: checá-las de novo
                // seria código morto disfarçado de cuidado.
                if (ligacao.situacao !== 'ATENDIDA') continue;
                // Dono explícito e sou eu. `idUsuario` nulo fica de fora de
                // propósito: sem saber de quem é a ligação, um card com
                // "Encerrar" poderia derrubar a chamada de um colega.
                if (!ligacao.idUsuario || ligacao.idUsuario !== eu) continue;
                lista.push(ligacao);
            }
            return lista;
        },
        /**
         * Tem sessão de áudio aberta? Separa "a ligação existe" de "eu ouço e
         * falo nela" — mutar e pôr em espera só fazem sentido no segundo caso.
         */
        temAudio(state) {
            return (idLigacao: string): boolean => state.emAndamento.includes(idLigacao);
        },
        /** A ligação em foco, se houver. */
        ativa(state): Ligacao | null {
            return state.emFoco ? (state.ligacoes[state.emFoco] ?? null) : null;
        },
        /**
         * Ligações VIVAS indexadas por atendimento — insumo do selo na lista de
         * conversas ("Chamando…" / "Ligação recebida" / "Em ligação").
         *
         * Terminal fica de fora mesmo continuando em `ligacoes`: a encerrada
         * permanece 6 s no card de propósito (o operador precisa VER acabar),
         * mas na lista isso viraria "em ligação" depois de a chamada cair.
         *
         * Uma ligação por atendimento, com `ATENDIDA` ganhando de `TOCANDO`:
         * o WhatsApp entrega ofertas concorrentes para o mesmo número, e o
         * estado que descreve melhor a conversa é o que já tem alguém falando.
         */
        vivasPorAtendimento(state): Record<string, Ligacao> {
            const eu = useAuthStoreUser().user?.id ?? null;
            const mapa: Record<string, Ligacao> = {};
            for (const ligacao of Object.values(state.ligacoes)) {
                if (!ligacao.idAtendimento) continue;
                if (TERMINAIS.includes(ligacao.situacao)) continue;
                // Discagem TOCANDO só aparece para quem discou. O evento é
                // publicado na sala da inbox inteira, e "Chamando…" na tela de
                // um colega é um selo que ele não pode nem atender nem tirar —
                // mesmo motivo pelo qual o getter `tocando` exclui REALIZADA.
                // Depois de ATENDIDA vale para todos: aí a conversa está mesmo
                // ocupada, e é isso que a lista precisa dizer.
                if (ligacao.situacao === 'TOCANDO'
                    && ligacao.direcao === 'REALIZADA'
                    && ligacao.idUsuario !== eu) continue;

                const atual = mapa[ligacao.idAtendimento];
                if (!atual || (atual.situacao === 'TOCANDO' && ligacao.situacao !== 'TOCANDO')) {
                    mapa[ligacao.idAtendimento] = ligacao;
                }
            }
            return mapa;
        },
    },

    actions: {
        /**
         * Assina `LIGACAO_ATUALIZADA`. Idempotente — o registro é global e
         * chamar duas vezes duplicaria os handlers.
         */
        registrar(websocketService: WebsocketService) {
            if (this.registrado) return;
            this.registrado = true;

            websocketService.registerCallback('LIGACAO_ATUALIZADA', (_payload) => {
                this.aplicar(_payload as unknown as LigacaoAtualizadaPayload);
            }, false, true);

            // Assinar não basta: o evento é publicado uma vez e não tem replay,
            // então uma ligação que já estava tocando quando esta aba abriu
            // nunca apareceria. Depois de assinar, e não antes — um evento que
            // chegue durante a busca é mais novo que ela, e `aplicar` já resolve
            // a ordem pela própria linha.
            void this.hidratar();
        },

        /**
         * Puxa do servidor as ligações vivas e as aplica como se tivessem
         * chegado pelo socket.
         *
         * O caminho é o MESMO do evento (`aplicar`), de propósito: filtro de
         * conta, watchdog de toque, prévia na lista e limpeza de terminal já
         * moram lá, e um segundo caminho de entrada seria um segundo lugar para
         * esquecer cada uma dessas regras.
         *
         * Falha em silêncio: isto é reconciliação, não o caminho principal. Sem
         * rede, o comportamento volta a ser exatamente o de antes de existir.
         */
        async hidratar(): Promise<void> {
            try {
                const vivas = await ligacoesService.ativas();
                for (const viva of vivas) this.aplicar(viva);
            } catch (e) {
                console.warn('[LIGACAO] falha ao reconciliar ligações vivas', e);
            }
        },

        /**
         * Aplica um evento do servidor.
         *
         * O socket entrega por sala de inbox, então um evento de inbox de outra
         * conta não pode pintar esta tela — mesma guarda que `aplicarPresencaChat`.
         */
        aplicar(payload: LigacaoAtualizadaPayload) {
            if (!payload?.idLigacao || !payload.idInbox) return;
            const authStore = useAuthStoreUser();
            if (!authStore.currentAccountInboxes?.some(i => i.id === payload.idInbox)) return;

            const anterior = this.ligacoes[payload.idLigacao];
            this.ligacoes[payload.idLigacao] = {
                ...anterior,
                idInbox:       payload.idInbox,
                idLigacao:     payload.idLigacao,
                idContato:     payload.idContato,
                idAtendimento: payload.idAtendimento,
                codigo:        payload.codigo,
                nome:          payload.nome,
                // `?? anterior`: a foto costuma vir no primeiro evento e o
                // servidor pode não repeti-la num evento posterior (contato
                // resolvido depois, servidor antigo). Sobrescrever com null
                // faria o avatar piscar de volta para as iniciais no meio da
                // ligação.
                imagem:        payload.imagem ?? anterior?.imagem ?? null,
                situacao:      payload.situacao,
                ehVideo:       payload.ehVideo,
                data:          payload.data,
                // `?? anterior` e não `?? 'RECEBIDA'`: a discagem já registrou a
                // linha como REALIZADA localmente, e um payload de servidor
                // antigo (sem o campo) não pode reescrevê-la como recebida — o
                // card viraria "Ligação recebida" com botão Atender no meio da
                // própria chamada.
                direcao:       payload.direcao ?? anterior?.direcao ?? 'RECEBIDA',
                idUsuario:     payload.idUsuario ?? anterior?.idUsuario ?? null,
                // `?? anterior` também aqui: um evento posterior que não traga
                // as datas (servidor antigo) não pode zerar o cronômetro de uma
                // ligação em curso.
                dataAtendimento: payload.dataAtendimento ?? anterior?.dataAtendimento ?? null,
                duracaoSegundos: payload.duracaoSegundos ?? anterior?.duracaoSegundos ?? null,
            };

            // A conversa sobe na lista em TODA transição, TOCANDO inclusive: quem
            // liga tem de aparecer no topo enquanto o telefone ainda toca. O
            // instante é `dataFim` quando existe e `data` (o início) enquanto a
            // ligação vive — função do ESTADO, nunca do relógio, senão o replay
            // da fila offline do WhatsApp faria a lista dançar. Repetir o mesmo
            // evento regrava o mesmo valor, e a guarda de "só para frente" do
            // `marcarInteracaoDeLigacao` absorve o resto.
            //
            // `import()` e não import estático porque `chatStore` já importa
            // ESTE módulo (registra o socket): o ciclo até resolveria — os dois
            // lados só tocam no outro dentro de função —, mas o import tardio
            // mantém a dependência em UMA direção no grafo de módulos.
            // Fire-and-forget: a ordenação da lista não pode atrasar o card.
            // A prévia só no fim: é ali que o desfecho existe. Enquanto toca, o
            // selo ao vivo do card já diz o que está acontecendo.
            // Lê da linha JÁ MERGEADA, não do payload cru: `direcao` tem fallback
            // para o valor anterior (servidor antigo não publicava o campo), e
            // sem ele a discagem do próprio operador virava "perdida" no card.
            const linha = this.ligacoes[payload.idLigacao];
            const houveConversa = (linha.duracaoSegundos ?? 0) > 0
                || payload.motivo === 'accepted_elsewhere';
            const previa = TERMINAIS.includes(payload.situacao)
                ? {
                    texto: textoDeLigacao({
                        direcao: linha.direcao,
                        situacao: payload.situacao,
                        motivo: payload.motivo,
                        duracaoSegundos: linha.duracaoSegundos,
                        ehVideo: linha.ehVideo,
                    }),
                    // Espelha `previa-ligacao.util.ts`: o campo alimenta o
                    // "respondido?" do Radar, então é "houve conversa", não
                    // "quem originou" — discagem nossa sem resposta não
                    // respondeu ninguém.
                    isFromMe: houveConversa,
                }
                : null;

            void import('./chatStore').then(({ useChatStore }) => {
                useChatStore().marcarInteracaoDeLigacao(
                    payload.idAtendimento,
                    payload.dataFim ?? payload.data ?? null,
                    previa,
                );
            }).catch(e => console.warn('[LIGACAO] falha ao subir a conversa na lista', e));

            this.armarWatchdogDeToque(payload.idLigacao, payload.situacao);
            this.sincronizarChamando();

            if (!TERMINAIS.includes(payload.situacao)) return;

            // Encerrou: derruba o áudio se era a ligação ativa e agenda a saída
            // da tela. Não remove na hora de propósito — o operador precisa ver
            // "perdida"/"encerrada" acontecer, senão o card só desaparece.
            // Com tom: a ligação acabou (desligaram, recusaram, não atenderam).
            if (this.emAndamento.includes(payload.idLigacao)) this.pararAudio(payload.idLigacao, { comTom: true });
            const anteriorTimer = timersDeLimpeza.get(payload.idLigacao);
            if (anteriorTimer) clearTimeout(anteriorTimer);
            timersDeLimpeza.set(payload.idLigacao, setTimeout(() => {
                delete this.ligacoes[payload.idLigacao];
                timersDeLimpeza.delete(payload.idLigacao);
            }, MS_ANTES_DE_LIMPAR));
        },

        /**
         * Derruba sozinha a ligação que ficou TOCANDO além do que o WhatsApp
         * tolera. Ver `MS_MAX_TOCANDO`.
         *
         * Sai de cena pela mesma porta da encerrada (remover de `ligacoes`), e
         * não marcando como PERDIDA: quem decide situação é o servidor, e
         * inventar um estado aqui apareceria no card como se o WhatsApp tivesse
         * dito isso. Qualquer transição real cancela o watchdog.
         */
        armarWatchdogDeToque(idLigacao: string, situacao: SituacaoLigacao) {
            const anterior = timersDeToque.get(idLigacao);
            if (anterior) {
                clearTimeout(anterior);
                timersDeToque.delete(idLigacao);
            }
            if (situacao !== 'TOCANDO') return;

            timersDeToque.set(idLigacao, setTimeout(() => {
                timersDeToque.delete(idLigacao);
                const presa = this.ligacoes[idLigacao];
                // Só descarta se AINDA está tocando: atendida no meio do caminho
                // é uma ligação viva de verdade, com áudio.
                if (presa?.situacao !== 'TOCANDO') return;

                console.warn('[LIGACAO] sem notícia depois do toque; descartando', idLigacao);
                delete this.ligacoes[idLigacao];
                this.sincronizarChamando();
            }, MS_MAX_TOCANDO));
        },

        /**
         * Liga o "chamando" enquanto houver discagem DESTE operador tocando, e
         * desliga no resto. Reconciliação e não liga/desliga por evento: são
         * muitas portas (evento, resposta do `discar`, encerrar, watchdog), e
         * cada uma só precisa chamar isto depois de mexer no estado.
         *
         * `emAndamento` é o que restringe ao operador: a discagem de um colega
         * chega a todos da inbox pelo evento, mas só entra em `emAndamento` de
         * quem discou.
         */
        sincronizarChamando() {
            definirChamando(this.emAndamento.some(id => {
                const ligacao = this.ligacoes[id];
                // Em espera não toca: o operador está falando na outra ligação.
                return ligacao?.direcao === 'REALIZADA' && ligacao.situacao === 'TOCANDO' && !this.emEspera[id];
            }));
        },

        /**
         * Atende: ORDEM OBRIGATÓRIA — emite o token, abre o WebSocket, e só
         * então manda atender.
         *
         * Atender antes de o socket existir faz quem ligou ouvir silêncio (a
         * fonte de microfone do gateway entra em underrun) e o operador perder o
         * começo da fala (os frames chegam sem ouvinte e são descartados).
         */
        async atender(idLigacao: string) {
            const ligacao = this.ligacoes[idLigacao];
            // Só o duplo-atendimento da MESMA ligação é barrado. Atender uma
            // segunda ligação é permitido: a anterior vai para espera.
            if (!ligacao || audios.has(idLigacao)) return;
            // Antes de qualquer `await`: ainda dentro do clique (ver `prepararTons`).
            prepararTons();

            ligacao.erro = null;
            try {
                if (!await this.abrirAudio(idLigacao)) return;

                await ligacoesService.atender(ligacao.idInbox, idLigacao);
                ligacao.situacao = 'ATENDIDA';
            } catch (e) {
                ligacao.erro = (e as Error).message;
                this.pararAudio(idLigacao);
            }
        },

        /**
         * Emite o token, abre o WebSocket de áudio e assume o foco. Uso INTERNO
         * (`atender` e `discar`); não é ação de tela.
         *
         * `false` = não abriu por falta de URL no servidor; erro de rede sobe
         * como exceção, para quem chamou decidir o que fazer com o card.
         */
        async abrirAudio(idLigacao: string): Promise<boolean> {
            const ligacao = this.ligacoes[idLigacao];
            if (!ligacao) return false;

            const sessao = await ligacoesService.abrirSessao(ligacao.idInbox, idLigacao);
            if (!sessao.url) {
                // O whiz-server compõe esta URL a partir da `baseUrl` do
                // servidor Evolution, então ela é sempre presente em
                // operação normal. Chegar vazia significa servidor sem
                // baseUrl gravada — falha explícita em vez de adivinhar.
                ligacao.erro = 'Servidor Evolution sem URL configurada: não é possível abrir o áudio.';
                return false;
            }

            // Com ESPERA_AUTOMATICA, a(s) anterior(es) saem de cena antes de
            // a nova entrar. Sem ele, todas ficam ao vivo — ver o comentário
            // da constante para o que isso implica no áudio.
            if (ESPERA_AUTOMATICA) {
                for (const outra of this.emAndamento) this.porEmEspera(outra, true);
            }

            // `includes`: a discagem já registrou a ligação em andamento antes
            // de abrir o áudio (para o card existir mesmo se o áudio falhar).
            if (!this.emAndamento.includes(idLigacao)) this.emAndamento.push(idLigacao);
            this.emFoco = idLigacao;
            this.mutados[idLigacao] = false;
            this.emEspera[idLigacao] = false;

            const sessao_audio = new LigacaoAudio({
                url: sessao.url,
                token: sessao.token,
                onEstado: (estado, detalhe) => {
                    const atual = this.ligacoes[idLigacao];
                    if (!atual) return;
                    atual.audio = estado;
                    if (detalhe) atual.erro = detalhe;
                },
                // Qualquer sessão NÃO em espera move o medidor: o microfone é
                // o mesmo dispositivo físico, então todas reportam o mesmo
                // nível. Filtrar por foco apagaria o indicador quando há duas
                // ativas e nenhuma é "a" em foco.
                onNivel: nivel => {
                    if (!this.emEspera[idLigacao]) this.nivelMic = nivel;
                },
            });
            audios.set(idLigacao, sessao_audio);
            await sessao_audio.iniciar();
            return true;
        },

        /**
         * Põe uma ligação em espera (ou retoma). Em espera: microfone e
         * reprodução suspensos, socket ABERTO — fechar perderia a ligação.
         */
        porEmEspera(idLigacao: string, valor: boolean) {
            audios.get(idLigacao)?.espera(valor);
            this.emEspera[idLigacao] = valor;
            this.sincronizarChamando();
            if (valor && this.emFoco === idLigacao) this.emFoco = null;
            if (!valor) this.emFoco = idLigacao;
        },

        /**
         * Traz uma ligação para foco, mandando as outras para espera. É o
         * "alternar" de um telefone com duas linhas.
         */
        focar(idLigacao: string) {
            if (!audios.has(idLigacao)) return;
            for (const outra of this.emAndamento) {
                if (outra !== idLigacao) this.porEmEspera(outra, true);
            }
            this.porEmEspera(idLigacao, false);
        },

        async recusar(idLigacao: string) {
            const ligacao = this.ligacoes[idLigacao];
            if (!ligacao) return;
            try {
                await ligacoesService.recusar(ligacao.idInbox, idLigacao);
            } catch (e) {
                ligacao.erro = (e as Error).message;
            }
        },

        async encerrar(idLigacao: string) {
            const ligacao = this.ligacoes[idLigacao];
            if (!ligacao) return;
            // Áudio primeiro: o operador solta o microfone na hora, mesmo que a
            // chamada HTTP demore ou falhe.
            this.pararAudio(idLigacao, { comTom: true });
            try {
                await ligacoesService.encerrar(ligacao.idInbox, idLigacao);
            } catch (e) {
                ligacao.erro = (e as Error).message;
                // O card SAI mesmo com o HTTP falhando.
                //
                // Não existe watchdog para `ATENDIDA` — só `TOCANDO` tem um —,
                // então uma linha atendida cujo evento terminal se perdeu ficava
                // na tela para sempre, com o cronômetro correndo, e `encerrar`
                // não a derrubava porque só o servidor muda `situacao`. Quando o
                // servidor É a peça quebrada, isso é um card eterno que só o
                // recarregar da página tira.
                //
                // Quem pediu para encerrar foi o operador; o card já cumpriu o
                // papel. Se a ligação estiver mesmo viva, o próximo evento do
                // servidor a traz de volta por `aplicar` — e a hidratação
                // também, no próximo carregamento.
                this.descartar(idLigacao);
            }
        },

        /**
         * Tira a ligação da tela sem afirmar nada sobre o estado dela no
         * servidor. Usado quando o operador já decidiu e o servidor não
         * confirmou — ver `encerrar`.
         */
        descartar(idLigacao: string) {
            const anterior = timersDeLimpeza.get(idLigacao);
            if (anterior) clearTimeout(anterior);
            timersDeLimpeza.set(idLigacao, setTimeout(() => {
                delete this.ligacoes[idLigacao];
                timersDeLimpeza.delete(idLigacao);
            }, MS_ANTES_DE_LIMPAR));
        },

        /**
         * Disca e ABRE O ÁUDIO. Devolve o `idLigacao` (o `call-id`) ou `null`
         * quando já havia discagem em voo nesta inbox.
         *
         * Erro da discagem SOBE (número fora do WhatsApp, 409 de "já está em
         * ligação", inbox sem ligações): quem chamou mostra a mensagem. Falha
         * só do ÁUDIO não sobe — a chamada está viva no telefone do cliente e o
         * card tem de existir para o operador poder encerrar.
         *
         * A ordem aqui é o inverso de `atender` (token → socket → ação) e não
         * pode ser outra: o `call-id` só existe depois de o gateway discar. A
         * janela é o tempo de toque, ou seja, segundos — mas se o cliente
         * atender rápido demais, os primeiros quadros de fala se perdem.
         */
        async discar(
            idInbox: string,
            numero: string,
            idAtendimento?: string | null,
            imagemContato?: string | null,
        ): Promise<string | null> {
            if (this.discando[idInbox]) return null;
            // Antes de qualquer `await`: ainda dentro do clique (ver `prepararTons`).
            prepararTons();
            this.discando[idInbox] = true;
            try {
                const linha = await ligacoesService.discar(idInbox, numero, idAtendimento);
                const idLigacao = linha.idExterno;

                // Registrada ANTES do áudio, e a partir da resposta em vez de
                // esperar o `LIGACAO_ATUALIZADA`: o socket pode chegar depois, e
                // até lá o operador ficaria sem nada na tela — com uma ligação
                // real tocando no telefone do cliente.
                this.ligacoes[idLigacao] = {
                    ...this.ligacoes[idLigacao],
                    idInbox,
                    idLigacao,
                    idContato:     linha.idContato,
                    idAtendimento: linha.idAtendimento,
                    // O servidor não devolve o contato resolvido aqui; o número
                    // discado é o rótulo até o evento trazer nome e código.
                    codigo:        this.ligacoes[idLigacao]?.codigo || numero,
                    nome:          this.ligacoes[idLigacao]?.nome ?? null,
                    // Foto de quem CHAMOU a ação (o chat aberto), não buscada
                    // aqui: a resposta de `discar` é a linha de `ligacoes`, não o
                    // contato, e o evento com a foto só chega frações de segundo
                    // depois — tempo suficiente para o avatar aparecer como
                    // iniciais e trocar na cara do operador.
                    imagem:        this.ligacoes[idLigacao]?.imagem ?? imagemContato ?? null,
                    situacao:      linha.situacao,
                    ehVideo:       linha.ehVideo,
                    data:          linha.dataInicio,
                    direcao:       linha.direcao,
                    idUsuario:     linha.idUsuario,
                    // `?? anterior` pelo mesmo motivo de `aplicar`: o evento da
                    // própria discagem é publicado pelo servidor ANTES de a
                    // resposta HTTP voltar, então um `accept` que chegue nessa
                    // janela já trouxe a âncora — zerar aqui deixava o
                    // cronômetro do card parado em branco pelo resto da ligação.
                    dataAtendimento: this.ligacoes[idLigacao]?.dataAtendimento ?? null,
                    duracaoSegundos: this.ligacoes[idLigacao]?.duracaoSegundos ?? null,
                    erro:          null,
                };
                if (!this.emAndamento.includes(idLigacao)) this.emAndamento.push(idLigacao);
                this.emFoco = idLigacao;
                this.mutados[idLigacao] = false;
                this.emEspera[idLigacao] = false;
                this.sincronizarChamando();

                try {
                    await this.abrirAudio(idLigacao);
                } catch (e) {
                    const atual = this.ligacoes[idLigacao];
                    if (atual) atual.erro = `Ligação em curso, mas o áudio não abriu: ${(e as Error).message}`;
                }

                return idLigacao;
            } finally {
                delete this.discando[idInbox];
            }
        },

        alternarMute(idLigacao: string) {
            const novo = !this.mutados[idLigacao];
            this.mutados[idLigacao] = novo;
            audios.get(idLigacao)?.mutar(novo);
        },

        /**
         * Encerra o áudio de UMA ligação, ou de todas quando `idLigacao` é
         * omitido (unmount). Idempotente: chega pelo botão, pelo evento terminal
         * e pelo unmount.
         *
         * Ao fechar a que estava em foco, a próxima em espera é retomada — senão
         * o operador ficaria com uma ligação viva e MUDA, sem perceber.
         *
         * `comTom`: a ligação ACABOU — toca os três bipes de fim, como o
         * WhatsApp. Sem tom quando o áudio sai por outro motivo (atender
         * recusado, unmount).
         */
        pararAudio(idLigacao?: string, { comTom = false }: { comTom?: boolean } = {}) {
            if (!idLigacao) {
                definirChamando(false);
                for (const [, sessao] of audios) sessao.encerrar();
                audios.clear();
                this.emAndamento = [];
                this.emFoco = null;
                this.mutados = {};
                this.emEspera = {};
                this.nivelMic = 0;
                return;
            }

            const tinhaAudio = audios.has(idLigacao) || this.emAndamento.includes(idLigacao);
            audios.get(idLigacao)?.encerrar();
            audios.delete(idLigacao);
            this.emAndamento = this.emAndamento.filter(id => id !== idLigacao);
            if (comTom && tinhaAudio) tocarFim();
            this.sincronizarChamando();
            delete this.mutados[idLigacao];
            delete this.emEspera[idLigacao];

            if (this.emFoco === idLigacao) {
                this.emFoco = null;
                this.nivelMic = 0;
                // Retoma a próxima SÓ se ela estiver em espera. Sem o modo
                // automático nada fica em espera, e "retomar" uma que já está
                // ativa apenas roubaria o foco sem mudar áudio nenhum.
                const proxima = this.emAndamento[this.emAndamento.length - 1];
                if (proxima && this.emEspera[proxima]) this.porEmEspera(proxima, false);
                else if (proxima) this.emFoco = proxima;
            }
        },
    },
});
